import { css } from "styled-components";

export interface MediaViewportFitOptions {
  /** Width/height ratio. Pass a `var(--…)` for per-instance ratios so the class stays shared. */
  aspectRatio: number | string;
  maxHeight: string;
  /** Optional source-size ceiling. Omit it when the frame may fill past intrinsic media width. */
  intrinsicWidth?: string;
}

/**
 * Reuse the inline-size half of viewport fit for chrome that must track a fitted media frame.
 * The media frame still owns the block-size/aspect-ratio constraints through
 * `mediaViewportFitCss`; siblings such as captions only share its resolved width.
 */
export function mediaViewportFitWidthCss({
  aspectRatio,
  maxHeight,
  intrinsicWidth,
}: MediaViewportFitOptions) {
  const widthCaps = ["100%"];
  if (intrinsicWidth !== undefined) widthCaps.push(intrinsicWidth);
  widthCaps.push(`calc((${maxHeight}) * ${aspectRatio})`);

  return css`
    width: min(${widthCaps.join(", ")});
    max-width: 100%;
  `;
}

/**
 * Keep the inline and block constraints for a height-capped media frame together.
 * Callers opt into viewport fit by providing the required ratio and height cap; an
 * intrinsic width, when known, is only an additional ceiling.
 */
export function mediaViewportFitCss({
  aspectRatio,
  maxHeight,
  intrinsicWidth,
}: MediaViewportFitOptions) {
  return css`
    ${mediaViewportFitWidthCss({ aspectRatio, maxHeight, intrinsicWidth })}
    max-height: ${maxHeight};
    aspect-ratio: ${aspectRatio};
  `;
}
